import React, { useState } from "react";
import { Editor as TinymceEditor } from "@tinymce/tinymce-react";
import { IEditorProps } from "./Editor.types";
import juice from "juice";
import { useLoopPlugin } from "./plugins/Loop/useLoopPlugin";
import { LoopDialog, TableColumnDef } from "./plugins/Loop/Loop";
import { loopWidgetHander } from "./plugins/Loop/LoopWidgetHandler";
import { useSignaturePlugin } from "./plugins/Signature/useSignaturePlugin";
import { SignaturePickerDialog } from "./plugins/Signature/SignaturePickerDialog";

export const Editor: React.FC<IEditorProps> = ({
  content,
  onChange,
  enableLoop,
  showSignatureWidget,
  showFontOptions,
}) => {
  const editorRef = React.useRef<TinymceEditor | null>(null);

  const [loopPlugin, setLoopPlugin] = useState<{
    open: boolean;
    initialData: TableColumnDef;
    widgetId: string;
  } | null>(null);

  const [signatureOpen, setSignatureOpen] = useState<boolean>(false);

  useLoopPlugin(() =>
    setLoopPlugin({
      open: true,
      initialData: {
        tableName: "",
        columnName: "",
        list: [],
      },
      widgetId: "",
    })
  );

  useSignaturePlugin(() => setSignatureOpen(true));

  const processHtmlForEmail = (htmlContent: any) => {
    // Inline CSS styles and clean up the HTML
    const inlinedHtml = juice(htmlContent);
    return inlinedHtml;
  };

  const handleEditorChange = (newContent: any) => {
    if (typeof newContent !== "string") {
      console.error("Content is not a string:", newContent);
      return;
    }

    const processedContent = processHtmlForEmail(newContent);
    onChange(processedContent);
  };

  const handleInsertLoopText = (
    tableHtml: string,
    isEdit?: boolean,
    uniqueId?: string
  ) => {
    if (!editorRef.current) return;

    const editor = editorRef.current as any;
    const doc = editor.getDoc ? editor.getDoc() : editor.dom.doc;

    if (isEdit && uniqueId) {
      const oldTable = doc.querySelector(`table[data-widget-id="${uniqueId}"]`);
      if (oldTable) {
        const temp = document.createElement("div");
        temp.innerHTML = tableHtml.trim();
        const newTable = temp.querySelector("table");

        if (newTable) {
          // Replace the old table with the new one in the DOM
          oldTable.replaceWith(newTable);
        }
      }
    } else {
      // Insert new table
      editor.insertContent(`<p>&nbsp;</p>${tableHtml}`, { format: "raw" });
    }
  };

  const handleInsertSignature = (htmlContent: string) => {
    if (!editorRef.current) return;
    const editor = editorRef.current as any;
    editor.insertContent(htmlContent);
    setSignatureOpen(false);
  };

  return (
    <>
      <TinymceEditor
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        licenseKey="gpl"
        onInit={(_evt, editor) => {
          editorRef.current = editor as any;
          if (enableLoop) {
            loopWidgetHander(editorRef, (open, columns, widgetId) => {
              setLoopPlugin({
                open: true,
                initialData: columns,
                widgetId: widgetId,
              });
            });
          }
        }}
        value={content}
        init={{
          height: 500,
          menubar: false,
          plugins: "lists link image code loopPlugin table".concat(showSignatureWidget ? " signaturePlugin" : ""),
          toolbar:
            "table undo redo | ".concat(showFontOptions ? "blocks fontfamily fontsize | " : "").concat("bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | code | forecolor | image | link anchor ").concat(enableLoop ? '| loopButton':'').concat(showSignatureWidget ? '| signatureButton' : ''),
          content_style:
            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
          forced_root_block: "p", // Ensures paragraphs are used instead of divs
          link_context_toolbar: true,
          newline_behavior: "linebreak",
          remove_trailing_brs: true,
          // <--- Important: disables wrapping content in <p>
          force_br_newlines: true, // <--- Adds <br> on Enter
          force_p_newlines: false, // <--- Prevents <p> on Enter
          extended_valid_elements:
            "table[border|cellspacing|cellpadding|width|style],tr,td[style],th[style]",

          // ✅ Ensure paste/insert doesn't escape HTML
          valid_children: "+body[style],+body[table]",

          protect: [
            /\@\@startLoop1\([^\)]+\)/g, // protect @@startLoop1(...)
            /\@\@endLoop1/g, // protect @@endLoop1
          ],
            entity_encoding: 'raw',
  verify_html: false,
   relative_urls: false,
    remove_script_host: false,
  convert_urls: false,
  promotion: false
        }}
        onEditorChange={handleEditorChange}
      />
      {loopPlugin && (
        <LoopDialog
          open={loopPlugin.open}
          onInsert={handleInsertLoopText}
          widgetId={loopPlugin.widgetId}
          initialColumns={loopPlugin.initialData}
          onClose={() => {
            setLoopPlugin(null);
          }}
        />
      )}
      {signatureOpen && (
        <SignaturePickerDialog
          open={signatureOpen}
          onClose={() => setSignatureOpen(false)}
          onInsert={handleInsertSignature}
        />
      )}
    </>
  );
};